import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ExportPDFEmployerIncomeTaxDeclaration from './ExportPDFEmployerIncomeTaxDeclaration';

// eslint-disable-next-line react/prop-types
const EmployerIncomeTaxDeclaration = ({ booking_id }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Part I - Monthly Combined Income
    incomeRange: '',
    
    // Part II - Authorisation
    // Employer
    employerName: '',
    employerNric: '',
    employerNOA: '',
    employerSignature: '',
    employerDate: '',
    
    // Spouse
    spouseName: '',
    spouseNric: '',
    spouseNOA: '',
    spouseSignature: '',
    spouseDate: ''
  });

  const incomeRanges = [
    'Below $2,000',
    '$2,000-$2,499',
    '$2,500-$2,999',
    '$3,000-$3,499',
    '$3,500-$3,999',
    '$4,000-$4,999',
    '$5,000-$5,999',
    '$6,000-$7,999',
    '$8,000-$9,999',
    '$10,000-$12,499',
    '$12,500-$14,999',
    '$15,000-$19,999',
    '$20,000-$24,999',
    '$25,000 and above'
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const [agencyInfo, setAgencyInfo] = useState({});
  // Fetch existing income tax declaration data
  const fetchIncomeTaxData = async () => {
    if (!booking_id) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`external-forms/tax-declarations/${booking_id}/`);
      if (response.data) {
        setAgencyInfo({
          agencyAddress: response.data.agency_address || '',
          agencyPhone: response.data.agency_phone || ''
        });
        setFormData({
          incomeRange: response.data.income_range || '',
          employerName: response.data.employer_name || '',
          employerNric: response.data.employer_nric || '',
          employerNOA: response.data.employer_noa || '',
          employerSignature: response.data.employer_signature || '',
          employerDate: response.data.employer_date || '',
          spouseName: response.data.spouse_name || '',
          spouseNric: response.data.spouse_nric || '',
          spouseNOA: response.data.spouse_noa || '',
          spouseSignature: response.data.spouse_signature || '',
          spouseDate: response.data.spouse_date || ''
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('No existing income tax declaration found for booking_id:', booking_id);
      } else {
        console.error('Error fetching income tax declaration data:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIncomeTaxData();
  }, [booking_id]);

  const validateForm = () => {
    const newErrors = {};
    // All fields are optional
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setErrors({});
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const apiData = {
        booking: booking_id,
        income_range: formData.incomeRange,
        employer_name: formData.employerName,
        employer_nric: formData.employerNric,
        employer_noa: formData.employerNOA,
        employer_signature: formData.employerSignature,
        employer_date: formData.employerDate || null,
        spouse_name: formData.spouseName,
        spouse_nric: formData.spouseNric,
        spouse_noa: formData.spouseNOA,
        spouse_signature: formData.spouseSignature,
        spouse_date: formData.spouseDate || null
      };

      const response = await api.post(`external-forms/tax-declarations/${booking_id}/`, apiData);
      console.log('Income tax declaration submitted successfully:', response.data);
      alert('Income tax declaration submitted successfully!');
      
    } catch (error) {
      console.error('Error submitting income tax declaration:', error);
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          setErrors(error.response.data);
        } else {
          alert('Error submitting form: ' + error.response.data);
        }
      } else {
        alert('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const originalHandleSubmit = (e) => {
    e.preventDefault();
    console.log('Employer Income Tax Declaration Data:', formData);
    // Handle form submission here
  };

  const handleReset = () => {
    setFormData({
      incomeRange: '',
      employerName: '',
      employerNric: '',
      employerNOA: '',
      employerSignature: '',
      employerDate: '',
      spouseName: '',
      spouseNric: '',
      spouseNOA: '',
      spouseSignature: '',
      spouseDate: ''
    });
  };

  const handleDelete = async () => {
    if (!booking_id) {
      alert('No booking ID provided for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this Employer Income Tax Declaration record? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Deleting Employer Income Tax Declaration for booking_id:', booking_id);
      const response = await api.delete(`external-forms/tax-declarations/${booking_id}/`);
      console.log('Employer Income Tax Declaration deleted successfully:', response.data);
      
      // Show success message
      alert('Employer Income Tax Declaration record deleted successfully!');
      
      // Reset the form after deletion
      handleReset();
      
    } catch (error) {
      console.error('Error deleting Employer Income Tax Declaration:', error);
      alert('Employer Income Tax Declaration record deleted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { user } = useAuth();
  const handleDownloadPDF = async () => {
    setIsSubmitting(true);

    try {
      console.log('Generating Employer Income Tax Declaration PDF for booking_id:', booking_id);
      
      // Prepare the data for PDF generation
      const pdfData = {
        ...formData,
        booking_id: booking_id
      };

      // Generate PDF using React PDF
      const blob = await pdf(
        <ExportPDFEmployerIncomeTaxDeclaration
          user={user}
          data={pdfData}
          agencyInfo={agencyInfo}
        />
      ).toBlob();
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = booking_id 
        ? `Employer_Income_Tax_Declaration_${booking_id}_${today}.pdf`
        : `Employer_Income_Tax_Declaration_${today}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Employer Income Tax Declaration PDF generated and downloaded successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      
      <div className="page-wrapper" style={{ marginLeft: '0' }}>
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="content-page-header">
                    <h5>Annex A – Employer and Spouse Income Tax Declaration</h5>
                  </div>

                  {/* Loading Indicator */}
                  {isLoading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">Loading existing income tax declaration data...</span>
                    </div>
                  )}

                  {/* Form Content - Hidden during loading */}
                  <div style={{ display: isLoading ? 'none' : 'block' }}>
                  <form onSubmit={handleSubmit}>
                    {/* Part I - Monthly Combined Income */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Part I – Monthly Combined Income</h6>
                      </div>
                      <div className="card-body">
                        <p className="mb-3">Please tick the appropriate box:</p>
                        
                        <div className="row">
                          {incomeRanges.map((range, index) => (
                            <div key={range} className="col-lg-6 col-12">
                              <div className="form-check mb-3">
                                <input
                                  type="radio"
                                  name="incomeRange"
                                  value={range}
                                  checked={formData.incomeRange === range}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`income-${index}`}
                                />
                                <label className="form-check-label" htmlFor={`income-${index}`}>
                                  {range}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Part II - Authorisation */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Part II – Authorisation</h6>
                      </div>
                      <div className="card-body">
                        <p className="mb-4">Authorisation by Employer and/or Spouse to Comptroller of Income Tax:</p>

                        {/* Employer Section */}
                        <div className="card mb-4">
                          <div className="card-body">
                            <h6 className="mb-3">Employer</h6>
                            
                            <div className="row">
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>Name</label>
                                  <input
                                    type="text"
                                    name="employerName"
                                    value={formData.employerName}
                                    onChange={handleInputChange}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>NRIC / WP No / FIN</label>
                                  <input
                                    type="text"
                                    name="employerNric"
                                    value={formData.employerNric}
                                    onChange={handleInputChange}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="input-block mb-3">
                              <label>Income Tax Notice of Assessment No</label>
                              <input
                                type="text"
                                name="employerNOA"
                                value={formData.employerNOA}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>Signature</label>
                                  <input
                                    type="text"
                                    name="employerSignature"
                                    value={formData.employerSignature}
                                    onChange={handleInputChange}
                                    className="form-control"
                                    placeholder="Type name as signature"
                                  />
                                </div>
                              </div>
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>Date</label>
                                  <input
                                    type="date"
                                    name="employerDate"
                                    value={formData.employerDate}
                                    onChange={handleInputChange}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Spouse Section */}
                        <div className="card mb-4">
                          <div className="card-body">
                            <h6 className="mb-3">Spouse</h6>
                            
                            <div className="row">
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>Name</label>
                                  <input
                                    type="text"
                                    name="spouseName"
                                    value={formData.spouseName}
                                    onChange={handleInputChange}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>NRIC / WP No / FIN</label>
                                  <input
                                    type="text"
                                    name="spouseNric"
                                    value={formData.spouseNric}
                                    onChange={handleInputChange}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="input-block mb-3">
                              <label>Income Tax Notice of Assessment No</label>
                              <input
                                type="text"
                                name="spouseNOA"
                                value={formData.spouseNOA}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>

                            <div className="row">
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>Signature</label>
                                  <input
                                    type="text"
                                    name="spouseSignature"
                                    value={formData.spouseSignature}
                                    onChange={handleInputChange}
                                    className="form-control"
                                    placeholder="Type name as signature"
                                  />
                                </div>
                              </div>
                              <div className="col-lg-6 col-12">
                                <div className="input-block mb-3">
                                  <label>Date</label>
                                  <input
                                    type="date"
                                    name="spouseDate"
                                    value={formData.spouseDate}
                                    onChange={handleInputChange}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Buttons */}
                    <div className="row">
                      <div className="col-12">
                        <div className="btn-path text-end">
                          <button
                            type="button"
                            onClick={handleReset}
                            className="btn btn-cancel bg-primary-light me-3"
                            disabled={isSubmitting}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger me-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Deleting...' : 'Clean'}
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadPDF}
                            className="btn btn-success me-3"
                            disabled={isSubmitting}
                            title="Download PDF of current form data"
                          >
                            {isSubmitting ? 'Generating...' : (
                              <>
                                <i className="fas fa-download me-2"></i>
                                Download PDF
                              </>
                            )}
                          </button>
                          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    </div>
                    {errors.general && (
                      <div className="alert alert-danger mt-3">{errors.general}</div>
                    )}
                  </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployerIncomeTaxDeclaration;